import React, { ReactNode } from 'react';
import { AutoSizer, List, ListRowProps, WindowScroller } from 'react-virtualized';
import clsx from 'clsx';

import * as styles from './virtualized-card-list.scss';
import { VirtualizedCardListLoader } from './virtualized-card-list-loader';

interface VirtualizedCardListProps<T> {
  /** Data items to render */
  data: T[];
  /** Render function for each row — receives the item and its index */
  renderRow: (item: T, index: number) => ReactNode;
  /** Height of each row in pixels (fixed or dynamic per row) */
  rowHeight: number | ((params: { index: number }) => number);
  /** Content to display when data is empty */
  emptyState: ReactNode;
  /** Whether data is currently loading */
  isLoading?: boolean;
  /** Number of rows to render beyond the visible area */
  overscanRowCount?: number;
  /** Optional CSS class name for the container */
  className?: string;
}

const DEFAULT_OVERSCAN = 5; // Number of extra rows to render beyond the visible area (for smoother scrolling)

/**
 * Component for rendering a virtualized list, optimizing performance by only rendering visible items.
 * Displays a consistent empty state with title, description, and optional action when data is empty.
 */
export const VirtualizedCardList = <T,>({
  data,
  renderRow,
  rowHeight,
  emptyState,
  isLoading,
  overscanRowCount = DEFAULT_OVERSCAN,
  className
}: VirtualizedCardListProps<T>) => {
  const rowRenderer = ({ key, index, style }: ListRowProps) => {
    const item = data[index];

    return (
      <div key={key} style={{ ...style, paddingBottom: '8px', boxSizing: 'border-box' }}>
        {renderRow(item, index)}
      </div>
    );
  };

  if (isLoading && data.length === 0) {
    return (
      <div className={className}>
        <VirtualizedCardListLoader />
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return <>{emptyState}</>;
  }

  return (
    <div className={clsx(styles.tableContainer, className)} data-testid="virtualized-card-list">
      <WindowScroller>
        {({ height, isScrolling, onChildScroll, registerChild, scrollTop }) => (
          <div className={styles.windowScrollerWrapper}>
            <AutoSizer disableHeight>
              {({ width }) => (
                <div ref={registerChild as React.LegacyRef<HTMLDivElement>}>
                  <List
                    className={styles.virtualizedList}
                    autoHeight
                    height={height}
                    isScrolling={isScrolling}
                    rowCount={data.length}
                    rowHeight={rowHeight}
                    rowRenderer={rowRenderer}
                    onScroll={onChildScroll}
                    scrollTop={scrollTop}
                    width={width}
                    overscanRowCount={overscanRowCount}
                  />
                </div>
              )}
            </AutoSizer>
          </div>
        )}
      </WindowScroller>
    </div>
  );
};
